import { useQueries } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import { ArrowUpRight, CheckCircle2, Clock, Droplets } from "lucide-react";
import { useMemo, type ReactNode } from "react";
import { api, unwrap } from "@/api/client";
import {
  useOrganization,
  useRefillRequests,
  useRefills,
  useReportSummary,
  useSites,
} from "@/api/hooks";
import { qk } from "@/api/keys";
import type { Refill, Tank } from "@/api/types";
import { useActiveOrgId, useSession } from "@/auth/session";
import { PageHeader } from "@/components/app-shell";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  MicroLabel,
  Num,
} from "@/components/ui/primitives";
import { DataTable } from "@/components/ui/data-table";
import { LevelBar, SightGlass } from "@/components/ui/sight-glass";
import { EmptyState, ErrorState, LoadingBlock, LoadingRows } from "@/components/ui/states";
import { assessTanks, needsAttention, sortByUrgency, type RiskLevel, type TankRisk } from "@/domain/risk";
import { formatDate, formatLiters, formatNumber, formatRelativeDays } from "@/lib/format";
import { useI18n } from "@/i18n/provider";
import type { TKey } from "@/i18n/dict";

/**
 * The dashboard.
 *
 * An operations manager opens this at 7am with a coffee and one question: WHAT IS ABOUT TO
 * GO WRONG TODAY? So that is what the page opens with, and it is the only thing above the
 * fold — the sites that are about to run dry, the refills that are overdue, the requests
 * waiting on a decision. Everything below it is context, not headline.
 *
 * A wall of vanity stat cards would answer a question nobody asked. "Total refills: 1,247"
 * is not a thing anybody acts on, and a dashboard nobody acts on is a dashboard nobody
 * opens. The month's totals ARE here — at the bottom, where totals belong.
 *
 * COLOUR: `dry` (#E0857E) appears in exactly one situation on this page — a tank that is
 * actually running out of fuel. Not on a button, not on a heading, not on a chart. See
 * DESIGN.md §1: the moment `dry` means two things it means nothing.
 */

const RISK_TONE: Record<RiskLevel, "dry" | "low" | "idle" | "ok"> = {
  dry: "dry",
  low: "low",
  overdue: "idle",
  ok: "ok",
};

const RISK_LABEL: Record<RiskLevel, TKey> = {
  dry: "dash.dry",
  low: "dash.low",
  overdue: "dash.overdue",
  ok: "dash.healthy",
};

/**
 * The dashboard needs every tank in the org, and the spec has no `GET /v1/tanks` — tanks
 * are only listed under their site. So we fan out one request per site.
 *
 * That is an N+1, and it is flagged to the backend team. It is survivable here because the
 * fan-out is parallel, cached per site, and an org has tens of sites, not thousands. It is
 * still the wrong shape and the spec should grow an org-wide tank list.
 *
 * Every key is `qk.siteTanks(orgId, …)` — org-scoped, like everything else that touches
 * tenant data. Switching org must never show the previous org's tanks out of the cache.
 */
function useAllTanks(siteIds: string[]) {
  const orgId = useActiveOrgId();

  return useQueries({
    queries: siteIds.map((siteId) => ({
      queryKey: qk.siteTanks(orgId, siteId),
      queryFn: () => unwrap(api.GET("/v1/sites/{siteId}/tanks", { params: { path: { siteId } } })),
    })),
    combine: (results) => ({
      tanks: results.flatMap((r) => r.data?.data ?? []) as Tank[],
      isPending: results.some((r) => r.isPending),
      error: results.find((r) => r.error)?.error,
    }),
  });
}

function monthWindow(now: Date): { from: string; to: string } {
  const from = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1));
  const to = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 1));
  return { from: from.toISOString(), to: to.toISOString() };
}

/** The fleet table is one row per SITE — a site is what an operator drives a truck to. */
function worstTankPerSite(risks: TankRisk[]): TankRisk[] {
  const worst = new Map<string, TankRisk>();
  // `risks` arrives already sorted worst-first, so the first tank seen for a site IS its
  // worst one. No second comparison, no second definition of "worst" to drift out of sync.
  for (const risk of risks) {
    if (!worst.has(risk.site.id)) worst.set(risk.site.id, risk);
  }
  return [...worst.values()];
}

export function DashboardPage() {
  const { t, lang } = useI18n();
  const { me, can } = useSession();

  const sitesQuery = useSites({ status: "active", limit: 100 });
  const sites = useMemo(() => sitesQuery.data?.data ?? [], [sitesQuery.data]);

  const tanksQuery = useAllTanks(sites.map((s) => s.id));
  const orgQuery = useOrganization();

  const refillsQuery = useRefills({ limit: 100 });
  const requestsQuery = useRefillRequests({ status: "pending" });

  const now = useMemo(() => new Date(), []);
  const window_ = useMemo(() => monthWindow(now), [now]);
  const summaryQuery = useReportSummary(window_);

  const risks = useMemo<TankRisk[]>(() => {
    if (!orgQuery.data) return [];

    const tanksBySite = new Map<string, Tank[]>();
    for (const tank of tanksQuery.tanks) {
      const list = tanksBySite.get(tank.site_id) ?? [];
      list.push(tank);
      tanksBySite.set(tank.site_id, list);
    }

    const refillsByTank = new Map<string, Refill[]>();
    for (const refill of refillsQuery.data?.data ?? []) {
      const list = refillsByTank.get(refill.tank_id) ?? [];
      list.push(refill);
      refillsByTank.set(refill.tank_id, list);
    }

    return sortByUrgency(
      assessTanks({
        sites,
        tanksBySite,
        refillsByTank,
        orgLowThresholdPercent: orgQuery.data.settings.low_fuel_threshold_percent,
        orgRefillOverdueDays: orgQuery.data.settings.refill_overdue_days,
        now,
      }),
    );
  }, [sites, tanksQuery.tanks, refillsQuery.data, orgQuery.data, now]);

  const urgent = useMemo(() => risks.filter(needsAttention), [risks]);
  const fleet = useMemo(() => worstTankPerSite(risks), [risks]);
  const tankNames = useMemo(
    () => new Map(tanksQuery.tanks.map((tank) => [tank.id, tank.name])),
    [tanksQuery.tanks],
  );

  const pendingRequests = requestsQuery.data?.data ?? [];
  const recent = (refillsQuery.data?.data ?? []).slice(0, 6);

  const isPending =
    sitesQuery.isPending || tanksQuery.isPending || orgQuery.isPending || refillsQuery.isPending;
  const error = sitesQuery.error ?? tanksQuery.error ?? orgQuery.error ?? refillsQuery.error;

  // `dry` is the count of tanks genuinely running out — it is the only thing on this page
  // that may wear the colour, so the tally only wears it when one of them exists.
  const anyDry = urgent.some((risk) => risk.level === "dry");

  if (error) return <ErrorState error={error} onRetry={() => void sitesQuery.refetch()} />;

  return (
    <>
      <PageHeader
        title={t("dash.greeting", { name: me?.user.name.split(" ")[0] ?? "" })}
        description={t("app.tagline")}
        actions={
          can("refills:create") ? (
            <Button asChild>
              <Link to="/refills/new">
                <Droplets className="size-4" aria-hidden />
                {t("dash.recordRefill")}
              </Link>
            </Button>
          ) : null
        }
      />

      {/* ══ THE LEDE ══════════════════════════════════════════════════════════
          What is about to go wrong. This section IS the page; everything else
          on it is there because the operator asked a second question. */}
      <section aria-labelledby="attention" className="mb-8">
        <div className="mb-3 flex items-center gap-2">
          <h2 id="attention" className="text-sm font-semibold text-text weight-display">
            {t("dash.attention")}
          </h2>
          {urgent.length > 0 ? (
            <Badge tone={anyDry ? "dry" : "low"}>
              <Num>{formatNumber(lang, urgent.length)}</Num>
            </Badge>
          ) : null}
        </div>

        {isPending ? (
          <Card>
            <LoadingRows rows={2} />
          </Card>
        ) : urgent.length === 0 && pendingRequests.length === 0 ? (
          // An empty attention list is GOOD NEWS and reads like it. This is the sentence
          // the operator most wants to see, so it is said plainly and warmly — not as a
          // shrug, and not as an "empty table" apology.
          <Card>
            <EmptyState
              icon={<CheckCircle2 className="size-6 text-ok" />}
              title={t("dash.attentionEmpty")}
              body={t("dash.attentionEmptyBody")}
            />
          </Card>
        ) : (
          <Card className="overflow-hidden">
            <ul>
              {urgent.map((risk) => (
                <AttentionRow key={risk.tank.id} risk={risk} />
              ))}
            </ul>

            {/* Requests are people waiting on YOU. They belong with the problems, not
                filed away on another screen where nobody looks until Thursday. */}
            {pendingRequests.length > 0 ? (
              <div className="flex flex-wrap items-center gap-x-3 gap-y-2 border-t border-rule bg-paper-sunk px-4 py-2.5">
                <Clock className="size-4 shrink-0 text-mauve" aria-hidden />
                <MicroLabel>{t("dash.pendingRequests")}</MicroLabel>
                <Num className="text-sm font-medium text-text">
                  {formatNumber(lang, pendingRequests.length)}
                </Num>
                <span className="min-w-0 flex-1 truncate text-xs text-muted">
                  {pendingRequests
                    .slice(0, 2)
                    .map((request) => request.site.name)
                    .join("، ")}
                </span>
                <Button asChild variant="ghost" size="sm">
                  <Link to="/requests">
                    {t("dash.reviewRequest")}
                    <ArrowUpRight className="size-3.5" aria-hidden />
                  </Link>
                </Button>
              </div>
            ) : null}
          </Card>
        )}
      </section>

      {/* ══ THE FLEET ═════════════════════════════════════════════════════════
          Every site, its worst tank, sortable. The scan you do after you have
          dealt with the fires. */}
      <section aria-labelledby="fleet" className="mb-8">
        <Card>
          <CardHeader>
            <CardTitle id="fleet">{t("dash.fleet")}</CardTitle>
            <Button asChild variant="ghost" size="sm">
              <Link to="/sites">
                {t("dash.viewAll")}
                <ArrowUpRight className="size-3.5" aria-hidden />
              </Link>
            </Button>
          </CardHeader>

          <FleetTable
            rows={fleet}
            isLoading={isPending}
            empty={
              <EmptyState
                title={t("sites.empty")}
                body={t("sites.emptyBody")}
                action={
                  can("sites:write") ? (
                    <Button asChild size="sm">
                      <Link to="/sites">{t("sites.new")}</Link>
                    </Button>
                  ) : null
                }
              />
            }
          />
        </Card>
      </section>

      {/* ══ RECENT ACTIVITY ═══════════════════════════════════════════════════
          The last litres that went into a tank, and who put them there. The
          ledger is the product's memory; this is the last page of it. */}
      <section aria-labelledby="recent" className="mb-8">
        <Card>
          <CardHeader>
            <CardTitle id="recent">{t("dash.recent")}</CardTitle>
            <Button asChild variant="ghost" size="sm">
              <Link to="/refills">
                {t("dash.viewAll")}
                <ArrowUpRight className="size-3.5" aria-hidden />
              </Link>
            </Button>
          </CardHeader>

          {isPending ? (
            <LoadingRows rows={3} />
          ) : recent.length === 0 ? (
            <EmptyState
              title={t("refills.empty")}
              body={t("refills.emptyBody")}
              action={
                can("refills:create") ? (
                  <Button asChild size="sm">
                    <Link to="/refills/new">{t("dash.recordRefill")}</Link>
                  </Button>
                ) : null
              }
            />
          ) : (
            <ul>
              {recent.map((refill) => (
                <li
                  key={refill.id}
                  className="flex flex-wrap items-baseline gap-x-3 gap-y-1 border-b border-rule px-4 py-2.5 last:border-b-0"
                >
                  <span className="text-sm font-medium text-text">{refill.site.name}</span>
                  <span className="text-xs text-faint">
                    {tankNames.get(refill.tank_id) ?? t("refills.tank")}
                  </span>

                  <span className="flex-1" />

                  {/* The litres are the fact. Everything else on the row is provenance. */}
                  <Num className="text-sm font-medium text-text">
                    +{formatLiters(lang, refill.added_liters)}
                  </Num>
                  <span className="text-xs text-faint">{t("common.liters")}</span>

                  {/* `<bdi>` around BOTH, and it is load-bearing.
                      An Arabic name is an RTL run; a date opening with "03" is a number. Put
                      them either side of a neutral "·" in an English (LTR) line and the bidi
                      algorithm drags the date's leading digits into the name's run — it
                      rendered as "Jul 2026 03 · عمر الجبوري" until this was here. `<bdi>` is
                      the element the HTML spec provides for exactly this: text of unknown
                      direction, isolated from its neighbours.

                      NOT `.num` on the date: in Arabic it carries a month NAME ("٠٣ تموز"),
                      and `.num` is mono + `direction: ltr` — a face with no Arabic glyphs.
                      `.num` is for litres, hours, percentages and counts. */}
                  <span className="w-full text-xs text-muted sm:w-auto sm:text-end">
                    <bdi>{refill.recorded_by.name}</bdi>
                    <span className="text-faint"> · </span>
                    <bdi className="text-faint">{formatDate(lang, refill.recorded_at)}</bdi>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </section>

      {/* ══ THE TOTALS ════════════════════════════════════════════════════════
          Real, and useful once a month. Not the headline, and they do not
          pretend to be — which is why they are the last thing on the page. */}
      <section aria-labelledby="month">
        <h2 id="month" className="mb-3 text-sm font-semibold text-text weight-display">
          {t("dash.thisMonth")}
        </h2>

        {summaryQuery.isPending ? (
          <Card>
            <LoadingBlock />
          </Card>
        ) : summaryQuery.error ? (
          <Card>
            <ErrorState error={summaryQuery.error} onRetry={() => void summaryQuery.refetch()} />
          </Card>
        ) : (
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <Stat
              label={t("dash.litersAdded")}
              value={formatLiters(lang, summaryQuery.data?.totals.liters_added ?? 0)}
              unit={t("common.liters")}
            />
            <Stat
              label={t("dash.refillsRecorded")}
              value={formatNumber(lang, summaryQuery.data?.totals.refills_recorded ?? 0)}
            />
            <Stat
              label={t("dash.litersPerHour")}
              value={
                summaryQuery.data?.totals.liters_per_hour !== undefined
                  ? formatNumber(lang, summaryQuery.data.totals.liters_per_hour, 1)
                  : "—"
              }
            />
            <Stat
              label={t("dash.sitesReporting")}
              value={formatNumber(lang, summaryQuery.data?.totals.sites_reporting ?? 0)}
            />
          </div>
        )}
      </section>
    </>
  );
}

/* ------------------------------------------------------------- attention */

/**
 * How long this tank has left, said the way an operator would say it out loud.
 *
 * The count comes from `Intl.RelativeTimeFormat`, not from a template with a bare number in
 * it. Arabic agrees its day-noun with the count — يوم / يومان / أيام / يومًا — so "خلال 4 يوم"
 * is simply wrong, and so is "خلال 25 أيام". Intl knows all of that in both languages, and
 * it throws in "today" / "اليوم" at zero for free.
 *
 * `bare` is for the table, where the column header already says "Time left": a row badged
 * **Healthy** must not also announce "Dry in 25 days". Same fact, and the framing is the
 * difference between information and a false alarm.
 */
function Runway({ risk, bare = false }: { risk: TankRisk; bare?: boolean }) {
  const { t, lang } = useI18n();

  if (risk.daysToDry !== null) {
    // Floor, not round: with 4.9 days of fuel you have four days to act, not five.
    const phrase = formatRelativeDays(lang, Math.floor(risk.daysToDry));
    return <>{bare ? phrase : t("dash.runsDry", { days: phrase })}</>;
  }

  if (risk.overdue && risk.daysSinceRefill !== null) {
    const phrase = formatRelativeDays(lang, -Math.round(risk.daysSinceRefill));
    return <>{bare ? phrase : t("dash.overdueBy", { days: phrase })}</>;
  }

  return <span className="text-faint">{t("dash.runsDryUnknown")}</span>;
}

/**
 * One row of trouble: the glass, the site, the level, the litres, the time left, the fix.
 *
 * A row and not a card, because the operator is comparing these against each other — which
 * site is worst? — and a grid of cards makes that comparison impossible. Rows line the
 * numbers up in a column, which is the entire reason numbers are set in a mono face.
 */
function AttentionRow({ risk }: { risk: TankRisk }) {
  const { t, lang } = useI18n();
  const { can } = useSession();

  return (
    <li
      className={
        "flex flex-wrap items-center gap-x-4 gap-y-3 border-b border-rule px-4 py-3.5 last:border-b-0 " +
        // The dry row is washed in the one warm colour in the product. It is the only
        // element on this page allowed to wear it.
        (risk.level === "dry" ? "bg-dry-wash/40" : "")
      }
    >
      <SightGlass
        percent={risk.percent}
        level={risk.level}
        className="h-12 w-5"
        label={`${risk.site.name} — ${risk.percent === null ? t("tank.levelUnknown") : `${Math.round(risk.percent)}%`}`}
      />

      {/* Who and where. */}
      <div className="min-w-0 flex-1 basis-40">
        <p className="truncate text-sm font-semibold text-text weight-display">{risk.site.name}</p>
        <p className="truncate text-xs text-muted">{risk.tank.name}</p>
      </div>

      {/* How much is left. The bar and the figure say the same thing two ways, because at
          7am one of them lands faster and it is not the same one for everybody. */}
      <div className="flex min-w-0 basis-48 flex-col gap-1.5">
        <LevelBar percent={risk.percent} level={risk.level} />
        <span className="text-[11px] text-faint">
          {risk.tank.current_level_liters !== undefined ? (
            <>
              {/* The unit stays OUTSIDE `.num`: "لتر" inside a `direction: ltr` mono span is
                  Arabic set in a face that has no Arabic. Only the figures go in. */}
              <Num>
                {formatLiters(lang, risk.tank.current_level_liters)} /{" "}
                {formatLiters(lang, risk.tank.capacity_liters)}
              </Num>{" "}
              {t("common.liters")}
            </>
          ) : (
            t("tank.levelUnknown")
          )}
        </span>
      </div>

      {/* How long it has. The one number that decides whether today is a bad day. */}
      <div className="flex basis-40 flex-col gap-1">
        <Badge tone={RISK_TONE[risk.level]} className="self-start">
          {t(RISK_LABEL[risk.level])}
        </Badge>
        <span className="text-xs text-muted">
          <Runway risk={risk} />
        </span>
      </div>

      <div className="flex shrink-0 items-center gap-2">
        {/* A Viewer cannot record a refill, so a Viewer is not shown a button that 403s. */}
        {can("refills:create") ? (
          <Button asChild size="sm" variant={risk.level === "dry" ? "primary" : "secondary"}>
            <Link to="/refills/new" search={{ tankId: risk.tank.id }}>
              {t("dash.recordRefill")}
            </Link>
          </Button>
        ) : null}
        <Button asChild size="sm" variant="ghost">
          <Link to="/sites/$siteId" params={{ siteId: risk.site.id }}>
            {t("dash.viewSite")}
          </Link>
        </Button>
      </div>
    </li>
  );
}

/* ----------------------------------------------------------------- fleet */

function FleetTable({
  rows,
  isLoading,
  empty,
}: {
  rows: TankRisk[];
  isLoading: boolean;
  empty: ReactNode;
}) {
  const { t, lang } = useI18n();

  const columns = useMemo<ColumnDef<TankRisk, unknown>[]>(
    () => [
      {
        id: "site",
        header: () => t("refills.site"),
        accessorFn: (risk) => risk.site.name,
        cell: ({ row }) => (
          <Link
            to="/sites/$siteId"
            params={{ siteId: row.original.site.id }}
            className="font-medium text-text underline-offset-4 hover:underline"
          >
            {row.original.site.name}
          </Link>
        ),
      },
      {
        id: "tank",
        header: () => t("refills.tank"),
        accessorFn: (risk) => risk.tank.name,
        cell: ({ row }) => <span className="text-muted">{row.original.tank.name}</span>,
      },
      {
        id: "level",
        header: () => t("tank.level"),
        // An unknown level sorts BELOW an empty tank rather than above a full one: a tank
        // nobody has read is a question, not a reassurance.
        accessorFn: (risk) => risk.percent ?? -1,
        cell: ({ row }) => {
          const risk = row.original;
          return (
            <div className="flex min-w-32 items-center gap-2">
              <LevelBar percent={risk.percent} level={risk.level} className="max-w-24" />
              <Num className="shrink-0 text-xs text-muted">
                {risk.percent === null ? "—" : `${formatNumber(lang, Math.round(risk.percent))}%`}
              </Num>
            </div>
          );
        },
      },
      {
        id: "remaining",
        header: () => t("dash.remaining"),
        accessorFn: (risk) => risk.tank.current_level_liters ?? -1,
        cell: ({ row }) => {
          const tank = row.original.tank;
          return tank.current_level_liters === undefined ? (
            <span className="text-xs text-faint">{t("tank.levelUnknown")}</span>
          ) : (
            <Num className="text-sm text-text">{formatLiters(lang, tank.current_level_liters)}</Num>
          );
        },
      },
      {
        id: "runway",
        header: () => t("dash.runway"),
        // Sorts soonest-empty first. A tank with no measured burn rate has no runway, so it
        // sorts last — it is not "fine", it is unknown, and the cell says so in words.
        accessorFn: (risk) => risk.daysToDry ?? Number.MAX_SAFE_INTEGER,
        cell: ({ row }) => (
          <span className="text-xs text-muted">
            <Runway risk={row.original} bare />
          </span>
        ),
      },
      {
        id: "status",
        header: () => t("refills.status"),
        accessorFn: (risk) => risk.level,
        cell: ({ row }) => (
          <Badge tone={RISK_TONE[row.original.level]}>{t(RISK_LABEL[row.original.level])}</Badge>
        ),
      },
    ],
    [t, lang],
  );

  return (
    <DataTable
      caption={t("dash.fleet")}
      columns={columns}
      data={rows}
      isLoading={isLoading}
      empty={empty}
    />
  );
}

/* ----------------------------------------------------------------- stats */

function Stat({ label, value, unit }: { label: string; value: string; unit?: string }) {
  return (
    <Card>
      <CardBody>
        <MicroLabel>{label}</MicroLabel>
        <p className="mt-2 text-2xl font-medium text-text">
          <Num>{value}</Num>
          {unit ? <span className="ms-1 text-sm text-faint">{unit}</span> : null}
        </p>
      </CardBody>
    </Card>
  );
}
